import React, { useState, useRef, useEffect, useMemo } from 'react';

import { useEsteiras } from '../../../hooks/useEsteiras';
import { getColaboradoresElegiveis } from '../../../api/especialidadesApi';
import {
  ESCOLHA_PADRAO,
  TEXTOS_ESCOLHA_COLABORADOR,
  type EscolhaColaborador,
} from '../../../shared/ui/escolha-colaborador';
import MotivoDaParada, {
  motivoDaParadaParaEnviar,
  pendenciaDoMotivo,
  useMotivosDaParada,
} from '../../motivo-da-parada/MotivoDaParada';
import './ApproveConfirmationChefeDeOficinaModal.css';

/**
 * at-68 (BUG-V06): serviço em aberto na esteira de ORIGEM, quando existe.
 *
 * `estado` vem de `atividade_carro` (at-46): `EM_ANDAMENTO` ou `PAUSADA`.
 */
export interface ServicoEmAbertoDaOrigem {
  esteiraId: string;
  estado?: string | null;
  esteiraNome?: string | null;
  /** at-134: o motivo que o colaborador declarou ao deixar o serviço inacabado (pré-preenche). */
  motivoParada?: { id: string; nome: string } | null;
  observacao?: string | null;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  message: string;
  /**
   * at-93: `deixarSemColaborador` = o Chefe escolheu "Não atribuir" (o padrão). Nunca vem junto
   * com `collaboratorId`; sem os dois, o Chefe escolheu manter quem já está no veículo.
   *
   * ⚠️ Antes da at-93 a AUSÊNCIA de `collaboratorId` significava "desvincular todos" para o
   * chamador — não havia como manter ninguém ao aprovar, e a tela ainda dizia que os
   * colaboradores continuariam (o defeito que esta entrega corrigiu).
   */
  onConfirm: (
    estimatedDaysInShop?: number,
    collaboratorId?: string,
    desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
    deixarSemColaborador?: boolean,
    /** at-134 (ADR 0065): por que o serviço parou, quando o desfecho é PAUSADO. */
    motivoDaParada?: { motivoParadaId?: string; pausaObservacao?: string },
  ) => void;
  onCancel: () => void;
  onReject: (collaboratorId?: string, deixarSemColaborador?: boolean) => void;
  /**
   * at-68 (BUG-V06): o serviço da esteira em que o veículo ESTÁ, quando está em aberto.
   *
   * Ao **mover** o veículo, o Chefe declara o desfecho desse serviço (concluído ou
   * inacabado). Ao **aprovar** uma mudança sugerida pelo colaborador, a tela não enviava
   * o campo e o backend assumia `CONCLUIDO` — e o `aplicarDesfecho` sobrescreve
   * `PAUSADA → CONCLUIDA`, então a aprovação **apagava em silêncio** o "inacabado" que o
   * colaborador havia declarado no `stop`. Ausente = não há serviço em aberto e a
   * pergunta não aparece.
   */
  servicoAtualEmAberto?: ServicoEmAbertoDaOrigem | null;
  initialEstimatedDaysInShop?: number;
  collaborators?: Array<{ id: string; fullName?: string; username?: string; email?: string }>;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  selectedStatus?: string;
  /**
   * at-62: colaboradores JÁ vinculados ao veículo, para o modal dizer quem CONTINUA e quem SAI ao
   * aprovar (pedido do Victor, 07/08/2026). Não confundir com `collaborators`, que é a lista de
   * candidatos do seletor. Ausente = o aviso não é exibido.
   */
  colaboradoresDoVeiculo?: Array<{ id: string; fullName?: string | null }>;
  /**
   * at-99: o veículo sendo aprovado. Com ele, o seletor de colaborador considera também as
   * esteiras abertas **em paralelo** — que continuam vigentes depois da aprovação, e é contra
   * esse conjunto que o backend julga o vínculo. Sem ele, quem só atende a paralela aparece
   * desabilitado num save que o backend aceitaria.
   */
  carId?: string | null;
}

const ConfirmationChefeDeOficinaModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  isLoading = false,
  message,
  onConfirm,
  onCancel,
  onReject,
  initialEstimatedDaysInShop,
  collaborators = [],
  colaboradoresDoVeiculo = [],
  servicoAtualEmAberto = null,
  sidebarRef,
  selectedStatus,
  carId = null,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { esteiras } = useEsteiras();
  // at-68 (BUG-V06): desfecho do serviço da esteira de origem — mesma escolha do modal
  // "Alterar esteira", que é o outro caminho pelo qual o veículo sai de uma esteira.
  const [desfechoServicoAtual, setDesfechoServicoAtual] = useState<'CONCLUIDO' | 'PAUSADO' | null>(null);
  // at-134 (ADR 0065): o motivo da parada quando o serviço da origem fica inacabado.
  const [motivoParadaId, setMotivoParadaId] = useState('');
  const [detalheDaParada, setDetalheDaParada] = useState('');
  const { motivos: motivosDaParada, estado: estadoDosMotivos } = useMotivosDaParada(isOpen && Boolean(servicoAtualEmAberto));
  const pendenciaDaParada =
    desfechoServicoAtual === 'PAUSADO'
      ? pendenciaDoMotivo(estadoDosMotivos, motivosDaParada, motivoParadaId, detalheDaParada)
      : null;
  const [estimatedDaysInShop, setEstimatedDaysInShop] = useState<number | undefined>();
  // Default: usar o SLA configurado na esteira destino (evita o "4" silencioso errado)
  const [useDefaultDays, setUseDefaultDays] = useState(true);
  /**
   * at-93: nasce em "Não atribuir", como nos outros dois fluxos de movimentação.
   *
   * Aqui isso é sobretudo TORNAR EXPLÍCITO o que já acontecia (aprovar sem escolher ninguém
   * desvinculava todos, via o aviso de "sem responsável" da tela do Chefe) — e ganha a opção que
   * faltava: "Manter os colaboradores atuais", que antes não existia neste modal.
   */
  const [escolhaColaborador, setEscolhaColaborador] = useState<EscolhaColaborador>(ESCOLHA_PADRAO);
  /**
   * at-62: ids elegíveis para a esteira que está sendo APROVADA (o destino do veículo). `null` =
   * sem restrição (regra inerte, ou consulta indisponível — fail-open).
   *
   * Sem isto, o select oferece a oficina inteira para uma esteira que talvez ninguém atenda: a
   * atribuição falharia depois, e o veículo iria para a esteira nova sem colaborador.
   */
  const [idsElegiveis, setIdsElegiveis] = useState<string[] | null>(null);
  const [elegibilidadeIndisponivel, setElegibilidadeIndisponivel] = useState(false);

  const targetEst = useMemo(
    () => esteiras.find((e) => e.id === selectedStatus || e.chave === selectedStatus || e.nome === selectedStatus),
    [esteiras, selectedStatus],
  );
  const defaultDays = targetEst?.slaDiasPadrao ?? 4;

  const semEspecialidade = (id: string) => idsElegiveis !== null && !idsElegiveis.includes(id);

  /**
   * Só quem pode trabalhar na esteira aprovada (decisão do Victor, 15/09/2026).
   *
   * Emenda a at-62, que os mantinha listados e desabilitados: numa oficina inteira dentro de um
   * `<select>`, a marca "(sem a especialidade desta esteira)" repetida em dezenas de linhas deixa
   * de informar e só atrapalha quem procura as duas pessoas que pode escolher.
   *
   * ⚠️ `idsElegiveis === null` mantém a lista completa — é a regra de especialidades inerte e o
   * fail-open da consulta que falhou. Filtrar ali esvaziaria o campo sem motivo.
   */
  const colaboradoresOferecidos = useMemo(
    () => collaborators.filter((c) => !semEspecialidade(c.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collaborators, idsElegiveis],
  );

  /**
   * at-62: destino que NÃO aceita colaborador não oferece o campo.
   *
   * Antes, aprovar para Entrega ou Aguardando Entrega mostrava o seletor, a pessoa era escolhida,
   * e o backend a desvinculava em silêncio um instante depois (`desvinculaAoAprovar`) — o usuário
   * ficava convencido de que havia deixado alguém no veículo. Medido em 07/08/2026: só
   * AGUARDANDO_PROXIMA_ETAPA, AGUARDANDO_ENTREGA e ENTREGA recusam; **ORCAMENTO aceita**, então o
   * fluxo "Responsável de orçamento" segue intacto.
   *
   * `undefined` (esteira não encontrada na lista) mantém o campo visível — na dúvida, preserva o
   * comportamento anterior em vez de esconder uma ação legítima.
   */
  const mostrarCampoColaborador = targetEst?.permiteColaborador !== false;

  /**
   * at-93: sem ninguém vinculado, "Não atribuir" e "Manter os atuais" descrevem o mesmo estado —
   * a segunda opção não é oferecida.
   */
  const podeManterColaboradores = colaboradoresDoVeiculo.length > 0;
  const colaboradorEscolhido =
    escolhaColaborador.modo === 'ADICIONAR' ? escolhaColaborador : null;

  /** "A", "A e B", "A, B e C". */
  const listarNomes = (nomes: string[]): string =>
    nomes.length <= 1
      ? nomes[0] ?? ''
      : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;

  /**
   * at-62: o que acontece com quem JÁ está no veículo quando a esteira for aprovada.
   *
   * A metade "quem continua" era silenciosa: o Chefe aprovava a mudança sem perceber que estava
   * deixando alguém trabalhando na esteira nova. Espelha o mesmo bloco do modal "Alterar esteira".
   */
  const efeitoNosColaboradores = useMemo(() => {
    if (!colaboradoresDoVeiculo.length) return null;

    const nomeDe = (c: { fullName?: string | null }) => c.fullName ?? 'Colaborador';
    const destinoNome = targetEst?.nome ?? 'a esteira aprovada';

    /**
     * ⚠️ Este ramo vem ANTES do de "Não atribuir": com esteira que não aceita colaborador o campo
     * de escolha não é exibido, e o que importa dizer é o MOTIVO, não a escolha que o Chefe não
     * fez. (Mesma ordem do modal "Alterar esteira".)
     *
     * ⚠️ Frases de gênero NEUTRO ("sai do veículo"): o sistema não sabe o gênero de quem está no
     * cadastro, e "Maria será desvinculado" lê como defeito.
     */
    if (targetEst?.permiteColaborador === false) {
      return [
        `${listarNomes(colaboradoresDoVeiculo.map(nomeDe))} ` +
          `${colaboradoresDoVeiculo.length === 1 ? 'sai' : 'saem'} do veículo: a esteira ` +
          `${destinoNome} não aceita colaborador.`,
      ];
    }

    /**
     * at-93: com "Não atribuir" (o padrão), TODOS saem — e é isso que o aviso diz.
     *
     * Este ramo corrige um defeito real: até a at-93 o texto afirmava "João continuará como
     * responsável" enquanto o fluxo do Chefe, ao aprovar sem ninguém escolhido, chamava o
     * desvínculo de todos. A tela dizia uma coisa e o sistema fazia outra.
     */
    if (escolhaColaborador.modo === 'NAO_ATRIBUIR') {
      return [
        `${listarNomes(colaboradoresDoVeiculo.map(nomeDe))} ` +
          `${colaboradoresDoVeiculo.length === 1 ? 'sai' : 'saem'} do veículo, que segue em ` +
          `${destinoNome} sem colaborador. As horas em aberto vão para aprovação.`,
      ];
    }

    if (elegibilidadeIndisponivel) {
      return [
        `O veículo tem ${listarNomes(colaboradoresDoVeiculo.map(nomeDe))} vinculado(s), mas não foi ` +
          `possível verificar agora quem tem a especialidade de ${destinoNome}. Confira o veículo ` +
          'depois de aprovar.',
      ];
    }

    const ficam = colaboradoresDoVeiculo.filter((c) => !semEspecialidade(c.id)).map(nomeDe);
    const saem = colaboradoresDoVeiculo.filter((c) => semEspecialidade(c.id)).map(nomeDe);
    const frases: string[] = [];

    if (ficam.length) {
      frases.push(
        `${listarNomes(ficam)} ${ficam.length === 1 ? 'continuará' : 'continuarão'} como ` +
          `${ficam.length === 1 ? 'responsável' : 'responsáveis'} pelo veículo em ${destinoNome}.`,
      );
    }
    if (saem.length) {
      frases.push(
        `${listarNomes(saem)} ${saem.length === 1 ? 'sai' : 'saem'} do veículo por não ter a ` +
          `especialidade de ${destinoNome}. As horas em aberto vão para aprovação.`,
      );
    }
    // at-93: a soma da at-45 dita em voz alta — escolher alguém não substitui quem continua.
    if (colaboradorEscolhido && ficam.length) {
      frases.push(
        `${colaboradorEscolhido.fullName ?? 'O colaborador escolhido'} entra no veículo, somado a ` +
          `quem continua — a escolha não substitui ${listarNomes(ficam)}.`,
      );
    }

    return frases.length ? frases : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    colaboradoresDoVeiculo,
    targetEst,
    idsElegiveis,
    elegibilidadeIndisponivel,
    escolhaColaborador,
  ]);

  const isOrcamentoTarget = selectedStatus === 'ORCAMENTO';
  const responsibleLabel = isOrcamentoTarget
    ? 'Responsável de orçamento'
    : TEXTOS_ESCOLHA_COLABORADOR.escolher.rotulo;
  const responsiblePlaceholder = isOrcamentoTarget
    ? 'Selecione uma pessoa de orçamento'
    : 'Selecione um colaborador';

  useEffect(() => {
    if (isOpen) {
      setUseDefaultDays(true);
      setEstimatedDaysInShop(
        typeof initialEstimatedDaysInShop === 'number' && initialEstimatedDaysInShop >= 1
          ? initialEstimatedDaysInShop
          : undefined,
      );
      // at-68: serviço já registrado como inacabado abre com "deixar inacabado" marcado —
      // é a continuidade do que já está valendo, e o Chefe pode trocar para concluído.
      // Mesma pré-seleção do modal "Alterar esteira".
      setDesfechoServicoAtual(servicoAtualEmAberto?.estado === 'PAUSADA' ? 'PAUSADO' : null);
      // at-134: o motivo que o colaborador declarou vem junto — o Chefe confirma ou troca.
      setMotivoParadaId(servicoAtualEmAberto?.estado === 'PAUSADA' ? servicoAtualEmAberto?.motivoParada?.id ?? '' : '');
      setDetalheDaParada(servicoAtualEmAberto?.estado === 'PAUSADA' ? servicoAtualEmAberto?.observacao ?? '' : '');
      // at-93: cada abertura recomeça no padrão seguro, nunca na escolha da vez anterior.
      setEscolhaColaborador(ESCOLHA_PADRAO);
      return;
    }
    setEstimatedDaysInShop(undefined);
    setUseDefaultDays(true);
    setEscolhaColaborador(ESCOLHA_PADRAO);
    setDesfechoServicoAtual(null);
    setMotivoParadaId('');
    setDetalheDaParada('');
    // `motivoParada`/`observacao` mudam junto com o `estado` (mesma linha do serviço).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialEstimatedDaysInShop, servicoAtualEmAberto?.estado]);

  // at-62: elegibilidade da esteira que será aprovada. Fail-open — falha deixa `null` e o aviso
  // abaixo do campo declara que não foi possível verificar.
  useEffect(() => {
    if (!isOpen || !targetEst?.id) {
      setIdsElegiveis(null);
      setElegibilidadeIndisponivel(false);
      return;
    }

    let cancelado = false;
    getColaboradoresElegiveis(targetEst.id, carId ?? undefined)
      .then(({ regraAtiva, elegiveis }) => {
        if (cancelado) return;
        setElegibilidadeIndisponivel(false);
        setIdsElegiveis(regraAtiva ? elegiveis.map((c) => c.id) : null);
      })
      .catch(() => {
        if (cancelado) return;
        setIdsElegiveis(null);
        setElegibilidadeIndisponivel(true);
      });

    return () => {
      cancelado = true;
    };
  }, [isOpen, targetEst?.id, carId]);

  // Escolha que deixou de ser válida (a esteira aprovada mudou) não pode ficar no campo. Idem
  // quando o campo deixa de ser exibido: valor invisível não pode ser enviado ao confirmar.
  useEffect(() => {
    if (!colaboradorEscolhido) return;
    if (!mostrarCampoColaborador || semEspecialidade(colaboradorEscolhido.collaboratorId)) {
      // at-93: volta ao padrão seguro, não a "manter" — a pessoa deixou de ser elegível, e nada
      // foi dito sobre quem já está no veículo.
      setEscolhaColaborador(ESCOLHA_PADRAO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsElegiveis, colaboradorEscolhido?.collaboratorId, mostrarCampoColaborador]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);
      if (isOutsideModal && isOutsideSidebar) onCancel();
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    else document.removeEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onCancel, sidebarRef]);

  if (!isOpen) return null;

  return (
    <div className="chefe-approve-overlay">
      <div className="chefe-approve-modal" ref={modalRef}>
        <div className="chefe-approve-header">
          <h3 className="chefe-approve-title">{message}</h3>
        </div>

        {isLoading ? (
          <div className="chefe-approve-loading">
            <div className="spinner" />
            <p>Carregando informações...</p>
          </div>
        ) : (
          <>
            <div className="chefe-approve-body">
              <div className="chefe-approve-field">
                <label>Estimativa de dias no setor</label>
                <label className="chefe-approve-default-days-toggle">
                  <input
                    type="checkbox"
                    checked={useDefaultDays}
                    onChange={(e) => {
                      setUseDefaultDays(e.target.checked);
                      if (!e.target.checked) setEstimatedDaysInShop(estimatedDaysInShop ?? defaultDays);
                    }}
                  />
                  <span>
                    Utilizar dias padrão da esteira{targetEst ? ` (${defaultDays} ${defaultDays === 1 ? 'dia' : 'dias'})` : ''}
                  </span>
                </label>
                {!useDefaultDays && (
                  <input
                    type="number"
                    min={1}
                    value={estimatedDaysInShop || ''}
                    onChange={(e) => setEstimatedDaysInShop(Number(e.target.value) || undefined)}
                    placeholder={`Ex: ${defaultDays}`}
                  />
                )}
              </div>

              {/* at-68 (BUG-V06): o veículo está saindo de uma esteira com serviço em aberto —
                  quem aprova declara o desfecho, exatamente como quem move pelo "Alterar
                  esteira". Sem esta pergunta o backend assumia CONCLUIDO, e o `aplicarDesfecho`
                  sobrescrevia o "inacabado" declarado pelo colaborador no `stop`. */}
              {servicoAtualEmAberto && (
                <div className="chefe-approve-servico-desfecho">
                  <label>
                    O que acontece com o serviço de{' '}
                    {servicoAtualEmAberto.esteiraNome || 'esteira atual'}?
                  </label>
                  {servicoAtualEmAberto.estado === 'PAUSADA' && (
                    <p className="chefe-approve-hint">
                      O colaborador registrou este serviço como inacabado.
                    </p>
                  )}
                  <div className="chefe-approve-desfecho-opcoes">
                    <button
                      type="button"
                      className={`chefe-approve-desfecho-opcao${desfechoServicoAtual === 'CONCLUIDO' ? ' selected' : ''}`}
                      onClick={() => setDesfechoServicoAtual('CONCLUIDO')}
                    >
                      <span className="chefe-approve-desfecho-nome">Serviço concluído</span>
                      <span className="chefe-approve-desfecho-desc">A etapa se encerra para o veículo</span>
                    </button>
                    <button
                      type="button"
                      className={`chefe-approve-desfecho-opcao${desfechoServicoAtual === 'PAUSADO' ? ' selected' : ''}`}
                      onClick={() => setDesfechoServicoAtual('PAUSADO')}
                    >
                      <span className="chefe-approve-desfecho-nome">Deixar inacabado</span>
                      <span className="chefe-approve-desfecho-desc">
                        Fica pendente e o veículo pode voltar para retomar
                      </span>
                    </button>
                  </div>
                  {desfechoServicoAtual === 'PAUSADO' && (
                    <MotivoDaParada
                      idPrefixo="chefe-approve-parada"
                      motivos={motivosDaParada}
                      estado={estadoDosMotivos}
                      motivoId={motivoParadaId}
                      onMotivo={setMotivoParadaId}
                      detalhe={detalheDaParada}
                      onDetalhe={setDetalheDaParada}
                    />
                  )}
                  {desfechoServicoAtual && (
                    <p className="chefe-approve-hint">
                      {desfechoServicoAtual === 'PAUSADO'
                        ? `O serviço de ${servicoAtualEmAberto.esteiraNome || 'esteira atual'} fica como pendência visível e retomável quando o veículo voltar.`
                        : `O serviço de ${servicoAtualEmAberto.esteiraNome || 'esteira atual'} é dado como realizado.`}
                    </p>
                  )}
                </div>
              )}

              {/* at-62: quem continua e quem sai do veículo ao aprovar. Fora do
                  `mostrarCampoColaborador` de propósito — destino que não aceita colaborador
                  esconde o seletor, e é justamente o caso em que avisar importa mais. */}
              {efeitoNosColaboradores && (
                <div className="chefe-approve-colaboradores-aviso">
                  {efeitoNosColaboradores.map((frase) => (
                    <p key={frase}>{frase}</p>
                  ))}
                </div>
              )}

              {mostrarCampoColaborador && (
              <div className="chefe-approve-field">
                {/* at-93: as opções explícitas vêm ANTES do select, e "Não atribuir" nasce
                    marcada. Reusa as classes de opção do desfecho de serviço (at-68), que já
                    vivem neste modal, em vez de criar visual novo. */}
                <label>O que acontece com os colaboradores do veículo</label>
                <div className="chefe-approve-desfecho-opcoes">
                  <button
                    type="button"
                    className={`chefe-approve-desfecho-opcao${escolhaColaborador.modo === 'NAO_ATRIBUIR' ? ' selected' : ''}`}
                    onClick={() => setEscolhaColaborador(ESCOLHA_PADRAO)}
                  >
                    <span className="chefe-approve-desfecho-nome">
                      {TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.rotulo}
                    </span>
                    <span className="chefe-approve-desfecho-desc">
                      {podeManterColaboradores
                        ? TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.descricaoComVinculados
                        : TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.descricaoSemVinculados}
                    </span>
                  </button>
                  {podeManterColaboradores && (
                    <button
                      type="button"
                      className={`chefe-approve-desfecho-opcao${escolhaColaborador.modo === 'MANTER' ? ' selected' : ''}`}
                      onClick={() => setEscolhaColaborador({ modo: 'MANTER' })}
                    >
                      <span className="chefe-approve-desfecho-nome">
                        {TEXTOS_ESCOLHA_COLABORADOR.manter.rotulo}
                      </span>
                      <span className="chefe-approve-desfecho-desc">
                        {TEXTOS_ESCOLHA_COLABORADOR.manter.descricao}
                      </span>
                    </button>
                  )}
                </div>

                <label>{responsibleLabel}</label>
                <select
                  value={colaboradorEscolhido?.collaboratorId ?? ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) {
                      setEscolhaColaborador(ESCOLHA_PADRAO);
                      return;
                    }
                    const escolhido = collaborators.find((c) => c.id === id);
                    setEscolhaColaborador({
                      modo: 'ADICIONAR',
                      collaboratorId: id,
                      fullName: escolhido?.fullName || escolhido?.username || null,
                    });
                  }}
                >
                  <option value="">{responsiblePlaceholder}</option>
                  {/* 15/09/2026: a lista traz APENAS quem atende a esteira aprovada (ou as
                      abertas em paralelo). Antes os demais apareciam desabilitados, com o motivo
                      ao lado — escolher alguém que o backend recusa deixaria o veículo na esteira
                      nova sem colaborador, e a lista longa escondia quem podia. */}
                  {colaboradoresOferecidos.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullName || c.username || c.email || 'Usuário'}
                    </option>
                  ))}
                </select>

                {idsElegiveis !== null && idsElegiveis.length === 0 && (
                  <p className="chefe-approve-hint">
                    Nenhum colaborador tem a especialidade de {targetEst?.nome ?? 'esta esteira'}, então
                    o veículo seguirá sem colaborador. Para liberar alguém, cadastre a especialidade em
                    Administração do Sistema.
                  </p>
                )}

                {elegibilidadeIndisponivel && (
                  <p className="chefe-approve-hint">
                    Não foi possível verificar as especialidades agora. A lista está completa; se o
                    colaborador escolhido não atender esta esteira, ele não será adicionado.
                  </p>
                )}
              </div>
              )}

            </div>

            <div className="chefe-approve-footer">
              <button className="chefe-approve-btn cancel" type="button" onClick={onCancel}>
                Cancelar
              </button>
              <button
                className="chefe-approve-btn reject"
                type="button"
                onClick={() =>
                  onReject(
                    colaboradorEscolhido?.collaboratorId,
                    escolhaColaborador.modo === 'NAO_ATRIBUIR' || undefined,
                  )
                }
              >
                Rejeitar
              </button>
              <button
                className="chefe-approve-btn confirm"
                type="button"
                // at-68 (BUG-V06): com serviço em aberto, o desfecho é obrigatório — igual ao
                // modal "Alterar esteira". Deixar passar sem escolha é o que fazia a aprovação
                // gravar "concluído" por omissão.
                disabled={
                  (!useDefaultDays && (!estimatedDaysInShop || estimatedDaysInShop < 1)) ||
                  (!!servicoAtualEmAberto && !desfechoServicoAtual) ||
                  Boolean(pendenciaDaParada)
                }
                title={
                  servicoAtualEmAberto && !desfechoServicoAtual
                    ? 'Escolha o que acontece com o serviço da esteira atual.'
                    : pendenciaDaParada ?? undefined
                }
                onClick={() => {
                  // at-134: o motivo só viaja quando há o que mandar (catálogo vazio = nada).
                  const motivo = motivoDaParadaParaEnviar(desfechoServicoAtual, motivoParadaId, detalheDaParada);
                  // undefined → o backend aplica o slaDiasPadrao da esteira destino (fallback 4)
                  onConfirm(
                    useDefaultDays ? undefined : estimatedDaysInShop,
                    colaboradorEscolhido?.collaboratorId,
                    desfechoServicoAtual ?? undefined,
                    // at-93: só quando o campo está visível — esteira que não aceita colaborador
                    // já desvincula todos no backend, e pedir o desvínculo por fora seria uma
                    // chamada a mais para o mesmo efeito.
                    (mostrarCampoColaborador && escolhaColaborador.modo === 'NAO_ATRIBUIR') ||
                      undefined,
                    ...(motivo ? [motivo] : []),
                  );
                }}
              >
                Confirmar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmationChefeDeOficinaModal;
